import { Box } from '@mui/material';
import TraceableLocation from 'assets/img/traceable-illustration.png';
import Loader from 'Components/Forms/Loader';
import usePageTitle from 'hooks/usePageTitle';
import { EQuestionnaireHeaderColumnEnum } from 'FeatureModules/Questionnaire/Components/QuestionnaireTable/questionnaireHeaderColumns';
import QuestionnaireTable from 'FeatureModules/Questionnaire/Components/QuestionnaireTable/QuestionnaireTable';
import { EmptyStateCard, ThemeButton } from 'DesignSystem';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import useQuestionnaires from 'FeatureModules/Questionnaire/hooks/useQuestionnaires';
import React, { FC } from 'react';
import { EBackgroundTypes, EItemTypes } from 'DesignSystem/types/background.types';
import { EAvailableSizes } from 'DesignSystem/types/size.types';
import ActionContainer from 'Components/ActionContainer';
import EmptyState from 'Components/EmptyState';
import ErrorState from 'Components/ErrorState';
import PageContainer from 'Components/PageContainer';
import PageTitle from 'Components/PageTitle';
import ScrollableTableContainer from 'Components/ScrollableTableContainer';

const Questionnaires: FC = () => {
  usePageTitle('Questionnaires');

  const {
    error,
    loading,
    questionnaires,
    viewOnly,
    handleQuestionnaireClick,
    handleCreateQuestionnaire,
    handleQuestionnaireEdit,
  } = useQuestionnaires();

  if (error) {
    return <ErrorState />;
  }
  if (loading) {
    return <Loader />;
  }

  if (!questionnaires.length) {
    return (
      <PageContainer>
        <PageTitle title="Questionnaires" />
        <Box mt={3}>
          {viewOnly ? (
            <EmptyState
              Icon={<img src={TraceableLocation} alt="traceable illustration" />}
              message="No questionnaires have been shared with you"
              additionalInfo="Once a partner requests you to complete a questionnaire (e.g. for compliance purposes), it will show up here."
            />
          ) : (
            <EmptyStateCard
              variant={EItemTypes.DOCUMENT}
              title="Create and request partners to complete questionnaires to assess their compliance"
              size={EAvailableSizes.LARGE}
              background={EBackgroundTypes.WORLD}
              buttonText="Create questionnaire"
              onCreateItemClick={handleCreateQuestionnaire}
            />
          )}
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageTitle title="Questionnaires" />
      <Box mb={3} />

      {!viewOnly && (
        <ActionContainer>
          <ThemeButton
            startIcon={<Icon name="edit" color="white" />}
            onClick={handleCreateQuestionnaire}
          >
            Create questionnaire
          </ThemeButton>
        </ActionContainer>
      )}

      <ScrollableTableContainer>
        <QuestionnaireTable
          questionnaires={questionnaires}
          columns={[
            EQuestionnaireHeaderColumnEnum.TITLE,
            EQuestionnaireHeaderColumnEnum.CREATED_BY,
            // uncomment if be added createdTimestamp to questionnaire
            // QuestionnaireHeaderColumnEnum.CREATED_AT,
            ...(viewOnly ? [] : [EQuestionnaireHeaderColumnEnum.ACTIONS]),
          ]}
          onSelect={handleQuestionnaireClick}
          onEdit={handleQuestionnaireEdit}
        />
      </ScrollableTableContainer>
    </PageContainer>
  );
};

export default Questionnaires;
